import cloneDeep from 'lodash.clonedeep'
import { ApplicantStageFilter, StepFilter } from 'types/graphql'

import { ApplicantStage } from 'src/graphql/types/applicants'

import { defaultFilters } from '../components/FiltersSiderbar/Filters'

export enum ApplicantFiltersActionType {
  FILTERS_CHANGED,
  FILTER_REMOVED,
  SEARCHBOX_CHANGED,
  DATE_FILTER_CHANGED,
  CLEAN_SEARCHBOX,
  CLEAN_RANGE_FILTER,
  CLEAN_MULTI_SELECT,
  CLEAN_STEP_FILTER,
  CLEAR_APPLICANT_STAGE_FILTER,
  CLEAN_LOCATION_FILTER,
  CLEAN_ZIPCODE_FILTER,
  CLEAR_ALL,
  FILTER_BY_HIRING_STAGE_METRIC,
  LOAD_JOB_LISTINGS,
  FILTER_BY_JOB_LISTING_ID,
  CUSTOM_FIELDS_CHANGED,
  CLEAR_CUSTOM_FIELD,
}

export const filtersReducer = (filters, action) => {
  const { payload } = action
  switch (action.type) {
    case ApplicantFiltersActionType.FILTERS_CHANGED: {
      return {
        ...payload,
      }
    }
    case ApplicantFiltersActionType.SEARCHBOX_CHANGED: {
      return {
        ...filters,
        searchText: { ...filters.searchText, searchText: payload.text },
      }
    }
    case ApplicantFiltersActionType.DATE_FILTER_CHANGED: {
      const key = payload.key || 'applicationDate'
      return {
        ...filters,
        // Saved views that predate a filter lack its type/key; fill from defaults
        [key]: {
          ...defaultFilters[key],
          ...filters[key],
          gte: payload.from,
          lte: payload.to,
        },
      }
    }
    case ApplicantFiltersActionType.CLEAN_SEARCHBOX: {
      return {
        ...filters,
        searchText: { ...filters.searchText, searchText: '' },
      }
    }
    case ApplicantFiltersActionType.CLEAN_RANGE_FILTER: {
      return {
        ...filters,
        [action.payload.filterKey]: {
          ...filters[action.payload.filterKey],
          gte: null,
          lte: null,
          gt: null,
          lt: null,
        },
      }
    }
    case ApplicantFiltersActionType.CLEAN_MULTI_SELECT: {
      const { key, value } = payload
      const valueIdx = filters[key].selectedValues.findIndex(
        (selectedValue) => value === selectedValue
      )

      const selectedValues = [...filters[key].selectedValues]
      selectedValues.splice(valueIdx, 1)

      const options = filters[key].options.map((option) => {
        if (option.value == value) {
          return {
            ...option,
            selected: false,
          }
        } else {
          return option
        }
      })

      return {
        ...filters,
        [key]: {
          ...filters[key],
          selectedValues,
          options,
        },
      }
    }
    case ApplicantFiltersActionType.CLEAN_STEP_FILTER: {
      const filtersCopy = cloneDeep(filters)
      const filter = payload

      const stepFilterValue = filter.value as StepFilter
      const customStepId = stepFilterValue.customStepId
      const stepType = stepFilterValue.type
      const subOption = stepFilterValue.status
      const stepKey = customStepId ?? stepType

      // Update the selected status of step types and their statuses in options
      filtersCopy.steps.options = filtersCopy.steps.options.map((option) => {
        if (option.value === stepKey) {
          const optionCopy = {
            ...option,
            options: option.options.map((statusOption) => ({
              ...statusOption,
              selected:
                statusOption.value === subOption
                  ? false
                  : statusOption.selected,
            })),
          }
          optionCopy.selected = optionCopy.options.some(
            (statusOption) => statusOption.selected
          )
          return optionCopy
        }
        return option
      })

      // Remove the selected status from the selectedValues
      filtersCopy.steps.selectedValues =
        filtersCopy.steps.selectedValues.filter(
          (selected) =>
            !(
              (selected.customStepId ?? selected.type) === stepKey &&
              selected.status === subOption
            )
        )
      return filtersCopy
    }
    case ApplicantFiltersActionType.CLEAR_APPLICANT_STAGE_FILTER: {
      const filtersCopy = cloneDeep(filters)
      const filter = payload

      const applicantStage = (filter.value as ApplicantStageFilter)
        .applicantStage

      filtersCopy.applicantStages.options =
        filtersCopy.applicantStages.options.map((option) => {
          if (option.value === applicantStage) {
            const optionCopy = {
              ...option,
              options: option.options?.map((subOption) => {
                if (
                  option.value === ApplicantStage.PROSPECT &&
                  subOption.value === filter.value.hiringStage
                ) {
                  return {
                    ...subOption,
                    selected: false,
                  }
                }
                if (
                  option.value === ApplicantStage.DISQUALIFIED &&
                  subOption.value === filter.value.disqualifiedReason
                ) {
                  return {
                    ...subOption,
                    selected: false,
                  }
                }
                if (
                  option.value === ApplicantStage.TERMINATED &&
                  subOption.value === filter.value.terminatedReason
                ) {
                  return {
                    ...subOption,
                    selected: false,
                  }
                }
                if (
                  option.value === ApplicantStage.NOT_INTERESTED &&
                  subOption.value === filter.value.notInterestedReason
                ) {
                  return {
                    ...subOption,
                    selected: false,
                  }
                }
                return subOption
              }),
            }
            optionCopy.selected = optionCopy.options.some(
              (subOption) => subOption.selected
            )
            return optionCopy
          }
          return option
        })

      // Remove the selected status from the selectedValues
      filtersCopy.applicantStages.selectedValues =
        filtersCopy.applicantStages.selectedValues.filter((selectedValue) => {
          // Only process items that match the applicant stage being removed
          if (selectedValue.applicantStage !== filter.value.applicantStage) {
            return true
          }

          if (selectedValue.applicantStage === ApplicantStage.PROSPECT) {
            return selectedValue.hiringStage !== filter.value.hiringStage
          }
          if (selectedValue.applicantStage === ApplicantStage.NOT_INTERESTED) {
            return (
              selectedValue.notInterestedReason !==
              filter.value.notInterestedReason
            )
          }
          if (selectedValue.applicantStage === ApplicantStage.DISQUALIFIED) {
            return (
              selectedValue.disqualifiedReason !==
              filter.value.disqualifiedReason
            )
          }
          if (selectedValue.applicantStage === ApplicantStage.TERMINATED) {
            return (
              selectedValue.terminatedReason !== filter.value.terminatedReason
            )
          }
          if (
            selectedValue.applicantStage === filter.value.applicantStage &&
            filter.value.applicantStage === ApplicantStage.HIRED
          ) {
            return false
          }
          return true
        })
      return filtersCopy
    }
    case ApplicantFiltersActionType.CLEAN_LOCATION_FILTER: {
      const filtersCopy = cloneDeep(filters)
      const filter = payload
      filtersCopy.zipCodes.values = filtersCopy.zipCodes.values.filter(
        (zipCode) => zipCode !== filter.value
      )
      return filtersCopy
    }
    case ApplicantFiltersActionType.CLEAN_ZIPCODE_FILTER: {
      const filtersCopy = cloneDeep(filters)
      const filter = payload
      filtersCopy.zipCodes.values = filtersCopy.zipCodes.values.filter(
        (zipCode) => zipCode !== filter.value
      )
      return filtersCopy
    }
    case ApplicantFiltersActionType.CLEAR_ALL: {
      const filtersCopy = cloneDeep(defaultFilters)

      // Reset applicant stages
      filtersCopy.applicantStages.options =
        defaultFilters.applicantStages.options.map((option) => {
          return {
            ...option,
            selected: false,
          }
        })
      filtersCopy.applicantStages.selectedValues = []

      // Reset zip codes
      filtersCopy.zipCodes.values = []

      // Reset equipment types
      filtersCopy.equipmentTypes.selectedValues = []
      filtersCopy.equipmentTypes.options =
        defaultFilters.equipmentTypes.options.map((option) => ({
          ...option,
          selected: false,
        }))

      // Reset recruiters
      filtersCopy.recruiters.selectedValues = []
      filtersCopy.recruiters.options = filtersCopy.recruiters.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset job listings
      filtersCopy.jobListings.selectedValues = []
      filtersCopy.jobListings.options = filtersCopy.jobListings.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset lead sources
      if (filtersCopy.leadSources) {
        filtersCopy.leadSources.selectedValues = []
        filtersCopy.leadSources.options = filtersCopy.leadSources.options.map(
          (option) => ({
            ...option,
            selected: false,
          })
        )
      }

      // Reset endorsements
      filtersCopy.endorsements.selectedValues = []
      filtersCopy.endorsements.options = filtersCopy.endorsements.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset tags
      filtersCopy.tags.selectedValues = []
      filtersCopy.tags.options = filtersCopy.tags.options.map((option) => ({
        ...option,
        selected: false,
      }))

      // Reset eligible for hire
      if (filtersCopy.eligibleForHire) {
        filtersCopy.eligibleForHire.selectedValues = []
        filtersCopy.eligibleForHire.options =
          filtersCopy.eligibleForHire.options.map((option) => ({
            ...option,
            selected: false,
          }))
      }

      // Reset permanently disqualified
      if (filtersCopy.permanentlyDisqualified) {
        filtersCopy.permanentlyDisqualified.selectedValues = []
        filtersCopy.permanentlyDisqualified.options =
          filtersCopy.permanentlyDisqualified.options.map((option) => ({
            ...option,
            selected: false,
          }))
      }

      // Reset years of experience range
      filtersCopy.yearsOfExperience.gte = null
      filtersCopy.yearsOfExperience.lte = null

      // Reset call count range
      filtersCopy.callCount.gte = null
      filtersCopy.callCount.lte = null

      // Reset missed call count range
      if (filtersCopy.missedOutboundCallCount) {
        filtersCopy.missedOutboundCallCount.gte = null
        filtersCopy.missedOutboundCallCount.lte = null
      }

      // Reset date ranges
      if (filtersCopy.applicationDate) {
        filtersCopy.applicationDate.gte = null
        filtersCopy.applicationDate.lte = null
      }
      if (filtersCopy.hiredDate) {
        filtersCopy.hiredDate.gte = null
        filtersCopy.hiredDate.lte = null
      }
      if (filtersCopy.availableForWorkDate) {
        filtersCopy.availableForWorkDate.gte = null
        filtersCopy.availableForWorkDate.lte = null
      }
      if (filtersCopy.terminatedDate) {
        filtersCopy.terminatedDate.gte = null
        filtersCopy.terminatedDate.lte = null
      }
      if (filtersCopy.disqualifiedDate) {
        filtersCopy.disqualifiedDate.gte = null
        filtersCopy.disqualifiedDate.lte = null
      }
      if (filtersCopy.notInterestedDate) {
        filtersCopy.notInterestedDate.gte = null
        filtersCopy.notInterestedDate.lte = null
      }
      if (filtersCopy.bookingDate) {
        filtersCopy.bookingDate.gte = null
        filtersCopy.bookingDate.lte = null
      }

      // Reset steps
      filtersCopy.steps.selectedValues = []
      filtersCopy.steps.options = filtersCopy.steps.options.map((option) => ({
        ...option,
        selected: false,
        options: option.options.map((subOption) => ({
          ...subOption,
          selected: false,
        })),
      }))

      // Reset search text
      filtersCopy.searchText.searchText = ''

      // Reset application date range
      filtersCopy.applicationDate.gte = null
      filtersCopy.applicationDate.lte = null
      if (filtersCopy.availableForWorkDate) {
        filtersCopy.availableForWorkDate.gte = null
        filtersCopy.availableForWorkDate.lte = null
      }

      // Reset custom fields
      filtersCopy.customFields = []

      return filtersCopy
    }
    case ApplicantFiltersActionType.FILTER_BY_HIRING_STAGE_METRIC: {
      // Clear all filters first, then apply the hiring stage filter
      const filtersCopy = cloneDeep(defaultFilters)

      // Reset all filters to default state
      filtersCopy.applicantStages.options =
        defaultFilters.applicantStages.options.map((option) => {
          return {
            ...option,
            selected: false,
          }
        })
      filtersCopy.applicantStages.selectedValues = []

      // Reset zip codes
      filtersCopy.zipCodes.values = []

      // Reset equipment types
      filtersCopy.equipmentTypes.selectedValues = []
      filtersCopy.equipmentTypes.options =
        defaultFilters.equipmentTypes.options.map((option) => ({
          ...option,
          selected: false,
        }))

      // Reset recruiters
      filtersCopy.recruiters.selectedValues = []
      filtersCopy.recruiters.options = filtersCopy.recruiters.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset job listings
      filtersCopy.jobListings.selectedValues = []
      filtersCopy.jobListings.options = filtersCopy.jobListings.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset endorsements
      filtersCopy.endorsements.selectedValues = []
      filtersCopy.endorsements.options = filtersCopy.endorsements.options.map(
        (option) => ({
          ...option,
          selected: false,
        })
      )

      // Reset tags
      filtersCopy.tags.selectedValues = []
      filtersCopy.tags.options = filtersCopy.tags.options.map((option) => ({
        ...option,
        selected: false,
      }))

      // Reset years of experience range
      filtersCopy.yearsOfExperience.gte = null
      filtersCopy.yearsOfExperience.lte = null

      // Reset call count range
      filtersCopy.callCount.gte = null
      filtersCopy.callCount.lte = null

      // Reset steps
      filtersCopy.steps.selectedValues = []
      filtersCopy.steps.options = filtersCopy.steps.options.map((option) => ({
        ...option,
        selected: false,
        options: option.options.map((subOption) => ({
          ...subOption,
          selected: false,
        })),
      }))
      // Reset search text
      filtersCopy.searchText.searchText = ''

      // Reset application date range
      filtersCopy.applicationDate.gte = null
      filtersCopy.applicationDate.lte = null

      // Now apply the hiring stage filter
      filtersCopy.applicantStages.options =
        filtersCopy.applicantStages.options.map((option) => {
          if (option.value === ApplicantStage.PROSPECT) {
            option.options = option.options.map((hiringStage) => {
              if (hiringStage.value === payload.name) {
                filtersCopy.applicantStages.selectedValues = [
                  {
                    applicantStage: ApplicantStage.PROSPECT,
                    hiringStage: payload.name,
                  },
                ]
                return {
                  ...hiringStage,
                  selected: hiringStage.value === payload.name,
                }
              }
              return {
                ...hiringStage,
                selectedValues: [],
                selected: false,
              }
            })
          }
          return option
        })
      return filtersCopy
    }
    case ApplicantFiltersActionType.CUSTOM_FIELDS_CHANGED: {
      return {
        ...filters,
        customFields: payload,
      }
    }
    case ApplicantFiltersActionType.CLEAR_CUSTOM_FIELD: {
      return {
        ...filters,
        customFields: filters.customFields?.filter(
          (f) => f.customFieldId !== payload
        ),
      }
    }
    default: {
      return filters
    }
  }
}
